import * as React from "react";
import { Search, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Input } from "@/components/atoms/input";
import { Button } from "@/components/atoms/button";

export interface SearchBarProps
  extends Omit<React.ComponentProps<"input">, "onChange"> {
  onSearch?: (value: string) => void;
  onClear?: () => void;
  debounceMs?: number;
}

function SearchBar({
  className,
  placeholder = "Search...",
  onSearch,
  onClear,
  debounceMs = 300,
  defaultValue = "",
  ...props
}: SearchBarProps) {
  const [value, setValue] = React.useState(String(defaultValue));
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onSearch?.(newValue);
    }, debounceMs);
  };

  const handleClear = () => {
    setValue("");
    onSearch?.("");
    onClear?.();
  };

  React.useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div className={cn("relative", className)}>
      <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
      <Input
        type="search"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        className="pl-9 pr-9"
        {...props}
      />
      {value && (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="absolute right-1 top-1/2 size-7 -translate-y-1/2"
          onClick={handleClear}
        >
          <X className="size-4" />
          <span className="sr-only">Clear</span>
        </Button>
      )}
    </div>
  );
}

export { SearchBar };
